import React, { createContext, useContext, useEffect, useState } from "react";
import { type User } from "firebase/auth";
import { 
  signInWithGoogle, 
  signInWithEmail, 
  registerWithEmail, 
  logOut, 
  getCurrentUser,
  getCurrentUserToken
} from "@/lib/firebase";
import { useToast } from "./use-toast";
import { useLocation } from "wouter";

interface AuthContextType {
  user: User | null;
  loading: boolean;
  getToken: () => Promise<string | null>;
  signInWithGoogle: (quizCode?: string) => Promise<void>;
  signInWithEmail: (email: string, password: string) => Promise<void>;
  registerWithEmail: (email: string, password: string, username: string) => Promise<void>;
  signOutUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();
  const [, setLocation] = useLocation();

  useEffect(() => {
    const initAuth = async () => {
      try {
        const user = await getCurrentUser();
        setUser(user);
      } catch (error) {
        console.error("Error initializing auth", error);
        toast({
          title: "Authentication Error",
          description: "Failed to initialize authentication",
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    };

    initAuth();
  }, [toast]);

  const getToken = async () => {
    return getCurrentUserToken();
  };

  const handleSignInWithGoogle = async (quizCode?: string) => {
    try {
      const user = await signInWithGoogle(quizCode);
      setUser(user);
    } catch (error) {
      console.error("Error signing in with Google:", error);
      toast({
        title: "Sign In Error",
        description: "Failed to sign in with Google",
        variant: "destructive",
      });
      throw error;
    }
  };

  const handleSignInWithEmail = async (email: string, password: string) => {
    try {
      const user = await signInWithEmail(email, password);
      setUser(user);
    } catch (error) {
      console.error("Error signing in with email:", error);
      toast({
        title: "Sign In Error",
        description: "Failed to sign in with email",
        variant: "destructive",
      });
      throw error;
    }
  };

  const handleRegisterWithEmail = async (email: string, password: string, username: string) => {
    try {
      const user = await registerWithEmail(email, password, username);
      setUser(user);
    } catch (error) {
      console.error("Error registering:", error);
      toast({
        title: "Registration Error",
        description: "Failed to register new account",
        variant: "destructive",
      });
      throw error;
    }
  };

  const handleSignOut = async () => {
    try {
      await logOut();
      setUser(null);
      setLocation("/"); // Redirect to homepage after logout
    } catch (error) {
      console.error("Error signing out:", error);
      toast({
        title: "Sign Out Error",
        description: "Failed to sign out",
        variant: "destructive",
      });
      throw error;
    }
  };

  const value = {
    user,
    loading,
    getToken,
    signInWithGoogle: handleSignInWithGoogle,
    signInWithEmail: handleSignInWithEmail,
    registerWithEmail: handleRegisterWithEmail,
    signOutUser: handleSignOut,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};